/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { shallowEqual, useIntl, useSelector } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { isEmpty, isFunction } from 'lodash-es';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { useThrottleFn, useControllableValue } from 'ahooks';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import useUploadImgHooks from './hooks/uploadImgHooks';
import ImgUploadRenderList from '../ImgUploadRenderList';
import { UPLOAD_FILE_TYPE_LIST, UPLOAD_FILE_IMG_TYPE_LIST } from '../../config/constant';
import DragDropUpload from '../DragDropUpload';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { useLocalState } from '../../ChatPage/hooks/useLocalState';
import AgentModeSelector from './components/AgentModeSelector';
import { useAgentModelsData } from '../../AgentCollection/hooks';
import { EAgentMentionCapability } from '../../AgentCollection/types';
import { hasAgentMentionCapability } from '../../AgentCollection/utils';
import { HIDDEN_ORGANIZATIONS } from '@/pages/aigc/components/OrganizationPermission';
import { type ISelectedSkillTagData } from './SkillMention/SelectedSkillTag';
import type { ISelectedChartTagData } from './ChartPopover/components/SelectedChartTag';
import SelectedChartTag from './ChartPopover/components/SelectedChartTag';
import useChartTags from './ChartPopover/hooks/useChartTags';
import { useChartMentionHost } from './ChartPopover/hooks/useChartMentionHost';
import { useSkillMentionHost } from './SkillMention/hooks/useSkillMentionHost';
import type { ICleanupTriggerBehavior } from './mentionShared/useMentionTrigger';
import { useKnowledgeMentionHost } from './KnowledgeBaseMention/hooks/useKnowledgeMentionHost';
import { useSkillMentionKeyboard } from './SkillMention/hooks/useSkillMentionKeyboard';
import { useMentionTextareaOrchestration } from './mentionShared/useMentionTextareaOrchestration';
import type { ISelectedKnowledgeTagData } from './KnowledgeBaseMention/components/SelectedKnowledgeTag';
import ExternalRefCard from './components/ExternalRefCard';
import {
  SKILL_AGENT_UUID,
  buildKnowledgeParams,
  buildLogParams,
  buildQueryChartsByGame,
  type TSubmitFrom,
} from '../../ChatPage/utils/submitHelpers';
import type {
  IQAInputProps,
  IQAInputRef,
  IQAInputSubmitTextOptions,
} from './types';
import { useSkillsData } from '../../SkillsCollection/hooks';
import type { ISkillData } from '../../SkillsCollection/types';
import useVoiceRecording from './hooks/useVoiceRecording';
import useTextareaAutoResize from './hooks/useTextareaAutoResize';
import usePlaceholder, { POSTER_DEFAULT_TEXT_ZH, POSTER_DEFAULT_TEXT_EN } from './hooks/usePlaceholder';
import {
  mergeQuickSubmitDisplayQuery,
  pruneSelectedByMissingLabels,
  isKbPrefillPlaceholderValue,
} from './utils/inlineMentionRanges';
import VoiceRecorder from './components/VoiceRecorder';
import SubmitActions from './components/SubmitActions';
import UploadFileButton from './components/UploadFileButton';
import {
  MentionEditor,
  type IMentionEditorSurface,
  isMentionEditorSurface,
} from './mentionEditor';
import { parseMentionBodyFromLabel, normalizeSerializedMentionGaps } from './mentionEditor/mentionTokenDom';
import { buildKnowledgeMentionKey } from './mentionEditor/mentionKeyUtils';
import { buildKbButtonPanelSerializedValue } from './KnowledgeBaseMention/utils/kbButtonPanelSync';
import { matchKnowledgeItemsForBody } from './mentionEditor/editorDom';
import {
  mentionLabelMatchVariants,
} from './utils/inlineMentionLabel';
import {
  removeInlineLabelFromValue,
  replaceTriggerOrInsertAt,
} from './mentionShared/mentionText';
import { QA_INPUT_DEFAULT_EDITOR_MIN_HEIGHT } from './constants';
import './index.scss';

// (Code Review Fix #2) 最大文本长度限制：2000字符
// 原因：后端 API 对单次对话输入有长度限制，超过此限制会导致请求失败
// 前端提前限制可以避免用户体验不佳的错误提示
const CHAT_BI_MAX_TEXT_COUNT = 2000;

const QAInput = forwardRef<IQAInputRef, IQAInputProps & Record<string, any>>((props, ref) => {
  const {
    className,
    minHeight = QA_INPUT_DEFAULT_EDITOR_MIN_HEIGHT,
    maxHeight = '182px',
    disabled = false,
    onSubmit,
    style,
    value = '',
    isProcessing,
    cancelRequest,
    lastMessages,
    sessionId,
    isEdit,
    editSessionKey,
    editHydrateEpoch,
    fileList,
    handleCancelEdit,
    canChangeMode = true,
    onTextInputHeightChange,
    maxFileSize = 5,
    maxCount = 5,
    acceptedFormats = UPLOAD_FILE_TYPE_LIST,
    disableDragUpload = false,
    dropAreaRef,
    voiceActiveBar = 175,
    from,
    taggingNode,
    currentSelectedDsModel,
    custVoiceStyle,
    retrying,
    customHeader,
    currentConversation,
    isPosterMode,
    selectedPosterTemplate,
    startupPrompt = true,
    serverParams,
    isShowDownload = true,
    copilotRefTagText,
    copilotRefTagShowClose,
    copilotRefTagHideArrow,
    copilotRefTagImage,
    onCopilotRefTagDismiss,
    onTaggingNodeClear,
    externalQueryConfig,
    isCopilot: isCopilotProp,
    sendDisabled: sendDisabledProp = false,
  } = props;
  const [inputValue, setInputValue] = useState(value || '');
  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const editorRef = useRef<IMentionEditorSurface>(null);
  const composingRef = useRef(false); // 是否处于输入法组合状态
  /** @button 打开面板时的插入偏移（关面板声明式写回 inputValue 用） */
  const kbPanelInsertOffsetRef = useRef(0);
  const kbPanelSelectedSnapshotRef = useRef<ISelectedKnowledgeTagData[]>([]);
  /** 知识库 @button 关面板重建 mention 期间，跳过 prune 避免误清空 */
  const kbPanelSyncLockRef = useRef(false);
  const { template, chatMode, tab } = useSearchParamsAppoint();

  const history: any = useHistory();
  // 使用 ref 保存被清空前的 uploadList，用于恢复
  const savedUploadListRef = useRef<any[]>([]);
  const isCopilot = isCopilotProp || from === 'copilot';

  /**
   * 统一的 template：优先使用 URL 参数，fallback 到全局会话模式状态
   */
  const [sessionTemplateMode, setSessionTemplateMode] = useLocalState<string>('__dba_session_template_mode__', '');
  const [newChatClickAt] = useLocalState<number>('__dba_home_new_chat_click_at__', 0);
  // 记录已处理的新对话点击时间戳，避免首次进入页面误触发清空
  const handledNewChatClickAtRef = useRef(newChatClickAt);
  const knowledgeFromEdit =
    isEdit && currentConversation?.metadata?.db_args?.enable === true;
  const effectiveTemplate = (
    template
    || sessionTemplateMode
    || (knowledgeFromEdit ? 'knowledge' : '')
  ) as string;
  const messageId = lastMessages?.messageId;
  const userInfo = useSelector(
    (state: any) => state?.home?.userInfo,
    shallowEqual,
  );
  const { currentData, allAgents, authorizedAgents } = useAgentModelsData();
  // 获取用户安装的 Skills（用于首页提交时的 allowed_skills）
  const { allowedSkillsForApi } = useSkillsData();
  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });
  const prevValueRef = useRef(value);
  const prevEditSessionKeyRef = useRef<number | undefined>(undefined);

  // 外部 value 变更时同步；kb 占位 `@-` 换成真实标签时，若用户已删掉占位则不再写回
  useEffect(() => {
    const prevValue = prevValueRef.current;
    prevValueRef.current = value;
    if (isKbPrefillPlaceholderValue(prevValue || '')) {
      if (isKbPrefillPlaceholderValue(value || '')) {
        setInputValue(value || '');
        return;
      }
      setInputValue((prev) => {
        if (isKbPrefillPlaceholderValue(prev)) {
          return value || '';
        }
        return prev;
      });
      return;
    }
    if (!value) return;
    setInputValue((prev) => (!prev.trim() || prev === prevValue ? value : prev));
  }, [value]);

  // ─── 录音相关逻辑（独立 hook） ───
  const {
    isRecording,
    speekLoading,
    isNoRecognize,
    voiceCountdownText,
    handleVoiceInput,
    confirmRecording,
    cancelRecording,
  } = useVoiceRecording({
    sessionId,
    from,
    messageId,
    userInfo,
    setInputValue,
  });

  const tagRef = useRef<HTMLSpanElement>(null);
  const [tagIndent, setTagIndent] = useState(0);
  const skillTagsRef = useRef<HTMLDivElement>(null);
  const editorRowRef = useRef<HTMLDivElement>(null);
  const [showNonSelectDSModelHint, setShowNonSelectDSModelHint] = useState(false);
  // 输入框容器 ref
  const inputBoxContainerRef = useRef<HTMLDivElement>(null);
  // Skill 下拉状态管理（由 useSkillMentionHost 统一管理，`/` 触发）
  const {
    wrapTextareaChange: skillWrapTextareaChange,
    skillMentionHostProps,
    isSlashModeOpen,
    cleanupSlashText,
    handleTextareaFocus: handleSlashTextareaFocus,
  } = useSkillMentionHost({
    inputValue,
    setInputValue,
    textareaRef: editorRef,
  });

  // 知识库 `@` 触发链路（对齐 Chart # 链路）
  const {
    wrapTextareaChange: kbWrapTextareaChange,
    knowledgeMentionHostProps,
    isAtModeOpen: isKbAtModeOpen,
    cleanupAtText: cleanupKbAtText,
    handleTextareaFocus: handleKbTextareaFocus,
  } = useKnowledgeMentionHost({
    inputValue,
    setInputValue,
    textareaRef: editorRef,
  });

  const handleSlashModeSelect = useCallback((
    insertLabel?: string,
    behavior?: ICleanupTriggerBehavior,
  ) => {
    const el = editorRef.current;
    if (el) {
      const root = isMentionEditorSurface(el) ? el.getRootElement() : null;
      if (root && document.activeElement !== root) {
        el.focus();
      }
    }
    cleanupSlashText(insertLabel, undefined, behavior);
  }, [cleanupSlashText]);

  // Chart 浮窗 # 触发链路（镜像 @ 三场景：① 新输入 # ② 删除后光标贴齐 # ③ focus 时贴齐 #）
  // 焦点保留通过 NewRegionSelect 的 disableAutoFocus prop 在源头根治，不需要 capture listener。
  const {
    wrapTextareaChange: chartWrapTextareaChange,
    chartMentionHostProps,
    cleanupHashText,
    handleTextareaFocus: handleHashTextareaFocus,
  } = useChartMentionHost({
    inputValue,
    setInputValue,
    textareaRef: editorRef,
  });

  // @ 模式下的过滤列表（由 SkillMention 通知更新）
  const [filteredSkills, setFilteredSkills] = useState<ISkillData[]>([]);
  const handleFilteredSkillsChange = useCallback((skills: ISkillData[]) => {
    setFilteredSkills((prev) => {
      if (prev === skills) return prev;
      if (prev.length !== skills.length) return skills;
      for (let i = 0;i < prev.length;i += 1) {
        if (prev[i]?.skill_id !== skills[i]?.skill_id) return skills;
      }
      return prev;
    });
  }, []);

  // / 模式下的键盘高亮索引
  const [slashHighlightIndex, setSlashHighlightIndex] = useState(0);

  useEffect(() => {
    if (!isSlashModeOpen) {
      setSlashHighlightIndex(0);
    }
  }, [isSlashModeOpen]);

  const [selectedKnowledgeRaw, handleSelectedKnowledgeChange] =
    useControllableValue<ISelectedKnowledgeTagData[]>(props, {
      defaultValue: [],
      valuePropName: 'selectedKnowledge',
      trigger: 'onSelectedKnowledgeChange',
    });

  const [selectedSkillsRaw, handleSelectedSkillsChange] =
    useControllableValue<ISelectedSkillTagData[]>(props, {
      defaultValue: [],
      valuePropName: 'selectedSkills',
      trigger: 'onSelectedSkillsChange',
    });

  // Home 传入 selectedSkills={undefined} 时 controllable 为受控空值，统一兜底
  const selectedKnowledge = selectedKnowledgeRaw ?? [];
  const selectedSkills = selectedSkillsRaw ?? [];
  const selectedKnowledgeRef = useRef(selectedKnowledge);
  selectedKnowledgeRef.current = selectedKnowledge;

  // 历史编辑：仅在 sessionKey / hydrateEpoch 变化时 hydrate（不监听 selectedKnowledge，避免面板改选被重置）
  useLayoutEffect(() => {
    if (!isEdit) {
      prevEditSessionKeyRef.current = undefined;
      return;
    }
    if (editSessionKey == null || editHydrateEpoch == null) return;
    if (editHydrateEpoch < 1) return;
    if (!value) return;
    if (kbPanelSyncLockRef.current) return;

    const sessionOpened = prevEditSessionKeyRef.current !== editSessionKey;
    prevEditSessionKeyRef.current = editSessionKey;

    if (sessionOpened) {
      setInputValue(value);
    }

    requestAnimationFrame(() => {
      const surface = editorRef.current;
      if (!isMentionEditorSurface(surface)) return;
      surface.forceHydrateFromValue(value, selectedKnowledgeRef.current, {
        cursorAtEnd: true,
      });
      surface.focus();
    });
  }, [isEdit, value, editSessionKey, editHydrateEpoch]);

  const handleKbAtModeSelect = useCallback((
    insertLabel?: string,
    addedItems?: ISelectedKnowledgeTagData[],
  ) => {
    cleanupKbAtText(
      insertLabel,
      addedItems?.length ? { knowledgeItems: addedItems } : undefined,
    );
  }, [cleanupKbAtText]);

  const handleKbBeforePanelOpen = useCallback(() => {
    const surface = editorRef.current;
    kbPanelSelectedSnapshotRef.current = [...selectedKnowledge];
    if (!isMentionEditorSurface(surface)) {
      if (kbPanelInsertOffsetRef.current <= 0) {
        kbPanelInsertOffsetRef.current = inputValue.length;
      }
      return;
    }
    const root = surface.getRootElement();
    const sel = window.getSelection();
    const inEditor = Boolean(
      root
      && sel
      && sel.rangeCount > 0
      && root.contains(sel.getRangeAt(0).startContainer),
    );
    if (inEditor) {
      surface.saveSelection();
      kbPanelInsertOffsetRef.current =
        surface.getPanelInsertOffset()
        ?? surface.selectionStart
        ?? inputValue.length;
      return;
    }
    kbPanelInsertOffsetRef.current = Math.min(
      kbPanelInsertOffsetRef.current,
      inputValue.length,
    );
  }, [inputValue, selectedKnowledge]);

  const handleKbInsertAtCursor = useCallback((
    insertLabel: string,
    addedItems?: ISelectedKnowledgeTagData[],
  ) => {
    const surface = editorRef.current;
    if (!isMentionEditorSurface(surface) || !insertLabel.trim()) return;
    const body = parseMentionBodyFromLabel(insertLabel, 'knowledge');
    const knowledgeItems = addedItems?.length
      ? addedItems
      : matchKnowledgeItemsForBody(body, selectedKnowledge, kbDocUnit);
    surface.insertMentionAtSelection(
      body,
      'knowledge',
      buildKnowledgeMentionKey(knowledgeItems),
      {
        usePinnedOffset: true,
        knowledgeItems: knowledgeItems.length ? knowledgeItems : undefined,
      },
    );
  }, [selectedKnowledge, kbDocUnit]);

  const handleKbPanelCommit = useCallback(() => {
    const surface = editorRef.current;
    if (isMentionEditorSurface(surface)) {
      surface.clearPanelInsertOffset();
    }
  }, []);

  /** 按钮模式：整份旧串 → 整份新串；首次选中则光标处插入 */
  const handleKbLabelReplace = useCallback((
    oldLabel: string,
    newLabel: string,
    nextKnowledgeItems?: ISelectedKnowledgeTagData[],
  ) => {
    const knowledgePool = nextKnowledgeItems ?? selectedKnowledge;
    const surface = editorRef.current;
    if (isMentionEditorSurface(surface)) {
      const newBody = parseMentionBodyFromLabel(newLabel, 'knowledge');
      const knowledgeItems = matchKnowledgeItemsForBody(
        newBody,
        knowledgePool,
        kbDocUnit,
      );
      const mentionOptions = knowledgeItems.length
        ? { knowledgeItems }
        : undefined;
      if (!oldLabel.trim()) {
        if (newLabel.trim()) {
          surface.insertMentionAtSelection(
            newBody,
            'knowledge',
            buildKnowledgeMentionKey(knowledgeItems),
            {
              ...mentionOptions,
              usePinnedOffset: true,
            },
          );
        }
        return;
      }
      surface.replaceMentionBody(
        parseMentionBodyFromLabel(oldLabel, 'knowledge'),
        newBody,
        'knowledge',
        mentionOptions,
      );
      return;
    }

    setInputValue((prev) => {
      const val = prev || '';
      const matchedOld = oldLabel
        ? mentionLabelMatchVariants(oldLabel.trim()).find((variant) => val.includes(variant))
        : undefined;
      if (matchedOld) {
        if (!newLabel) return removeInlineLabelFromValue(val, matchedOld);
        return val.replace(matchedOld, newLabel);
      }
      if (!newLabel) return val;
      const insert = newLabel.endsWith(' ') ? newLabel : `${newLabel} `;
      const { nextValue } = replaceTriggerOrInsertAt(val, insert, {
        selectionStart: val.length,
        selectionEnd: val.length,
      });
      return nextValue;
    });
  }, [setInputValue, selectedKnowledge, kbDocUnit]);

  // @ 模式键盘交互（独立 hook，职责单一）
  // 兼容 filteredSkills / filteredSkillsRef 两种参数形态，避免类型缓存导致的编译抖动
  const skillMentionKeyboardOptions = {
    isSlashModeOpen,
    filteredSkills,
    filteredSkillsRef: { current: filteredSkills },
    highlightIndex: slashHighlightIndex,
    setHighlightIndex: setSlashHighlightIndex,
    selectedSkills,
    onSelectedChange: handleSelectedSkillsChange,
    closeDropdown: () => skillMentionHostProps.onSkillDropdownOpenChange(false),
    cleanupSlashText,
    isEN,
  } as any;

  const { handleSlashModeKeyDown } = useSkillMentionKeyboard(skillMentionKeyboardOptions);

  // ─── Chart Tag 状态管理（独立 hook，与 QAInput 解耦） ───
  const {
    selectedCharts,
    setSelectedCharts,
    chartPopoverRef,
    handleChartConfirm: rawHandleChartConfirm,
    handleRemoveChartTag,
    handleChartTagClick,
    handleClearCharts,
  } = useChartTags(externalQueryConfig);

  // Skill / 图表：skill、aiAgent、ChatPage 首页 tab、Copilot 空会话（session 为空）
  const mentionTemplate = isCopilot ? sessionTemplateMode : effectiveTemplate;
  const isMentionScene =
    !taggingNode && (
      mentionTemplate === 'skill'
      || mentionTemplate === 'aiAgent'
      || (!isCopilot && tab === 'aiAgent')
      || (isCopilot && !lastMessages && !sessionTemplateMode)
    );

  const isSkillTemplate = effectiveTemplate === 'skill';
  const isKnowledgeTemplate = effectiveTemplate === 'knowledge';
  const hasSelectedSkills = selectedSkills.length > 0;
  const hasSelectedCharts = selectedCharts.length > 0;

  const shouldShowSkillMentionBtn =
    isMentionScene &&
    (hasAgentMentionCapability(currentData, EAgentMentionCapability.SKILL) ||
      isSkillTemplate);

  const shouldShowAgentSelectorBtn =
    template !== 'dsReport' &&
    (!taggingNode || isPosterMode) &&
    canChangeMode &&
    (authorizedAgents?.length ?? 0) > 1 &&
    !hasSelectedSkills &&
    !hasSelectedCharts &&
    !isSkillTemplate;

  const allowSlashMention = shouldShowSkillMentionBtn;

  const allowKnowledgeMention =
    (shouldShowAgentSelectorBtn
      || shouldShowSkillMentionBtn
      || isKnowledgeTemplate)
    && hasAgentMentionCapability(currentData, EAgentMentionCapability.KNOWLEDGE);

  const allowHashMention =
    isMentionScene &&
    hasAgentMentionCapability(currentData, EAgentMentionCapability.CHART);

  const {
    handleMentionTextareaChange,
    handleMentionFocusOrClick,
  } = useMentionTextareaOrchestration({
    allowKnowledgeMention,
    allowSlashMention,
    allowHashMention,
    isSlashModeOpen,
    isKbAtModeOpen,
    kbWrapTextareaChange,
    skillWrapTextareaChange,
    chartWrapTextareaChange,
    setInputValue,
    handleKbTextareaFocus,
    handleSlashTextareaFocus,
    handleHashTextareaFocus,
  });

  /**
   * Chart 确认：仅更新 selectedCharts；# 模式只清理触发符，不写正文。
   */
  const handleChartConfirm = useCallback(
    (newCharts: ISelectedChartTagData[], gameCode: string) => {
      rawHandleChartConfirm(newCharts, gameCode);
      if (chartMentionHostProps.chartMentionOpenReason === 'hash') {
        cleanupHashText();
      }
    },
    [
      rawHandleChartConfirm,
      cleanupHashText,
      chartMentionHostProps.chartMentionOpenReason,
    ],
  );

  // Home 场景点击“新增对话”时，统一清空 input / skill / chart tag；
  // 仅响应「新的点击事件」（时间戳递增），避免首次进入页面误清空
  useEffect(() => {
    if (from !== 'agentHome' || !newChatClickAt) return;
    if (newChatClickAt <= handledNewChatClickAtRef.current) return;
    handledNewChatClickAtRef.current = newChatClickAt;
    setInputValue('');
    handleSelectedSkillsChange([]);
    handleSelectedKnowledgeChange([]);
    handleClearCharts();
  }, [from, newChatClickAt, handleSelectedSkillsChange, handleSelectedKnowledgeChange, handleClearCharts]);

  // 顶栏 Skill tag 已下线；高度自适应仍消费固定零缩进 layout
  const skillTagsLayout = useMemo(
    () => ({ paddingTop: 0, lastLineWidth: 0 }),
    [],
  );
  const hasChartTags =
    !isRecording &&
    template !== 'dsReport' &&
    selectedCharts.length > 0;

  const handleEditorChange = useCallback((
    newValue: string,
    meta?: { anchor: number; focus: number; },
  ) => {
    const normalizedValue = normalizeSerializedMentionGaps(newValue);
    setInputValue(normalizedValue);

    const surface = editorRef.current;
    const anchor = meta?.anchor ?? surface?.selectionStart ?? normalizedValue.length;
    const focus = meta?.focus ?? surface?.selectionEnd ?? normalizedValue.length;
    const syntheticTarget: Partial<HTMLTextAreaElement> = {
      value: normalizedValue,
      selectionStart: anchor,
      selectionEnd: focus,
    };
    const syntheticEvent = {
      target: syntheticTarget,
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
    handleMentionTextareaChange(syntheticEvent);
  }, [handleMentionTextareaChange]);

  /** @button 关面板：声明式重建 inputValue + 强制 hydrate DOM */
  const handleKbPanelSync = useCallback((
    nextTags: ISelectedKnowledgeTagData[],
  ) => {
    kbPanelSyncLockRef.current = true;
    try {
      const surface = editorRef.current;
      const currentValue = isMentionEditorSurface(surface)
        ? (surface.value || inputValue)
        : inputValue;

      const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
      nextTags.forEach((item) => {
        const list = byKb.get(item.kbId) || [];
        list.push(item);
        byKb.set(item.kbId, list);
      });

      const groups = Array.from(byKb.values()).filter((group) => group.length > 0);
      const usedOffset = kbPanelInsertOffsetRef.current;
      const nextValue = normalizeSerializedMentionGaps(
        buildKbButtonPanelSerializedValue(
          currentValue,
          groups,
          kbDocUnit,
          usedOffset,
          kbPanelSelectedSnapshotRef.current,
        ),
      );

      const delta = nextValue.length - currentValue.length;
      kbPanelInsertOffsetRef.current = Math.min(
        Math.max(0, usedOffset + Math.max(0, delta)),
        nextValue.length,
      );
      kbPanelSelectedSnapshotRef.current = [];
      setInputValue(nextValue);

      if (isMentionEditorSurface(surface)) {
        surface.forceHydrateFromValue(nextValue, nextTags);
        surface.clearPanelInsertOffset();
      }
    } finally {
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            kbPanelSyncLockRef.current = false;
          });
        });
      });
    }
  }, [inputValue, kbDocUnit]);

  // 剪枝仅在正文变化时执行：避免 selectedSkills 先更新、inputValue 未同步时误删（根因）
  useEffect(() => {
    if (composingRef.current || isEdit) return;
    if (kbPanelSyncLockRef.current) return;
    // 占位 `@-` 尚未换成真实标签时不要剪枝，否则会把 URL 预填知识库清掉
    if (isKbPrefillPlaceholderValue(inputValue || '')) return;
    if (!selectedSkills.length && !selectedKnowledge.length) return;

    const pruned = pruneSelectedByMissingLabels({
      value: inputValue || '',
      selectedSkills,
      selectedKnowledge,
      isEN,
      kbDocUnit,
    });

    if (pruned.nextSkills.length !== selectedSkills.length) {
      handleSelectedSkillsChange(pruned.nextSkills);
    }
    if (pruned.nextKnowledge.length !== selectedKnowledge.length) {
      handleSelectedKnowledgeChange(pruned.nextKnowledge);
    }
    // 仅依赖 inputValue：选中 Skill/KB 会先写入正文，再由正文变更触发剪枝
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputValue,
    isEN,
    kbDocUnit,
    handleSelectedSkillsChange,
    handleSelectedKnowledgeChange,
    isEdit,
  ]);

  const initialHomeSessionId = useMemo(() => {
    if (isFunction(onSubmit)) return '';
    return uuidv4();
  }, [onSubmit]);

  const uploadLkeFileSession = sessionId || initialHomeSessionId;
  
  // 统一管理 disableFileUpload 状态
  const disableFileUpload = useMemo(() => {
    // 编辑态：从 allAgents 中根据 currentConversation.modeId 查找对应 agent
    if (isEdit && currentConversation?.modeId) {
      const targetAgent = allAgents?.find(
        (agent) => agent.agentId === currentConversation.modeId,
      );
      return targetAgent?.hideFileUpload || targetAgent?.disableFileUpload;
    }
    // 非编辑态：直接使用当前选中 Agent 的配置
    return currentData?.hideFileUpload || currentData?.disableFileUpload;
  }, [
    isEdit,
    currentConversation?.modeId,
    currentData?.agentId,
    currentData?.hideFileUpload,
    currentData?.disableFileUpload,
    allAgents,
  ]);

  const { handlePaste,
    uploadList,
    setUploadList,
    // errorL ist,
    successList,
    sendDisabled: uploadSendDisabled,
    // handleFileUpload,
    handleReUpload,
    handleRemoveItem,
    handleClickUpload,
    isDragging,
    imgUploadDisabled
  } = useUploadImgHooks({
    maxFileSize,
    acceptedFormats,
    fileList: isEdit ? fileList : [],
    isEdit,
    maxCount,
    disableDragUpload: disableDragUpload || isRecording || (taggingNode !== undefined && !isPosterMode) || template === 'dsReport' || disableFileUpload,
    isCopilot,
    dropAreaRef,
    initialHomeSessionId: uploadLkeFileSession
  });

  const sendDisabled = uploadSendDisabled || sendDisabledProp;

  // chart part 仅用于 edit 回传，与 ImgUploadRenderList 一致不参与附件区 UI
  const visibleUploadList = useMemo(
    () => (uploadList || []).filter((item: any) => item?.file?.mimeType !== 'chart'),
    [uploadList],
  );

  // 获取用户的组织列表
  const userOrganizations: string[] = userInfo?.organization || [];

  // 判断用户是否属于需要隐藏的组织
  const shouldHide = userOrganizations.some((org: string) =>
    HIDDEN_ORGANIZATIONS.includes(org),
  );

  // 监听 disableFileUpload 的变化，实现清空和恢复逻辑
  useEffect(() => {
    // 编辑态的情况下。历史数据不做清空
    if (isEdit) return;
    if (disableFileUpload) {
      // 当 disableFileUpload 为 true 时，保存当前数据并清空
      if (uploadList?.length > 0) {
        savedUploadListRef.current = uploadList;
        setUploadList([]);
      }
    } else {
      // 当 disableFileUpload 为 false 时，恢复之前保存的数据
      if (savedUploadListRef?.current?.length > 0) {
        setUploadList(savedUploadListRef?.current);
        // 恢复后清空 ref，避免重复恢复
        savedUploadListRef.current = [];
      }
    }
  }, [disableFileUpload, isEdit]);

  // 判断是否因为非图片文件且无文本输入而需要禁用发送
  const shouldDisableSendForNonImage = useMemo(() => {
    if (sendDisabled) return false;
    // 检查上传列表中的文件是否都是图片类型
    const isAllImages = visibleUploadList.every(item =>
      UPLOAD_FILE_IMG_TYPE_LIST.includes((item.type || '').toLowerCase()),
    );
    // 如果不全是图片且没有输入文本，则需要禁用发送
    if (!isAllImages && !inputValue) {
      return true;
    }
    return false;
  }, [sendDisabled, visibleUploadList, inputValue]);

  const showErrorTips = useErrorTips();

  // ─── Placeholder 文案计算（独立 hook） ───
  const { placeholderMessage } = usePlaceholder({
    isRecording,
    speekLoading,
    isNoRecognize,
    isPosterMode,
    template: template as string | undefined,
    selectedPosterTemplate,
    serverParams,
    shouldHide,
    currentData,
    taggingNode,
    startupPrompt,
    currentSelectedDsModel,
    allowSlashMention,
    allowKnowledgeMention,
    hasChartTags,
    inputValue,
    setInputValue,
  });

  // ─── textarea 高度自适应（独立 hook） ───
  useTextareaAutoResize({
    textareaRef: editorRef,
    editorRowRef,
    skillTagsRef,
    inputValue,
    maxHeight,
    minHeight,
    placeholderMessage,
    skillTagsLayout,
    onTextInputHeightChange,
    isEdit,
  });

  const { runAsync, loading } = useRequestController({
    method: 'POST',
    url: `/api/agent/chat_cancel`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
  });

  const handleSubmit = useCallback((textSubmit?: { text: string; } & IQAInputSubmitTextOptions): void => {
    const submitFrom: TSubmitFrom = textSubmit?.from ?? 'send';
    const isTextSubmit = Boolean(textSubmit?.text);
    const rawSubmitText = (textSubmit?.text ?? inputValue).trim();
    const editorSerializedValue = (() => {
      const surface = editorRef.current;
      if (isMentionEditorSurface(surface) && surface.value) {
        return surface.value;
      }
      return inputValue;
    })();
    const trimmed = isTextSubmit
      ? normalizeSerializedMentionGaps(
        mergeQuickSubmitDisplayQuery(editorSerializedValue, rawSubmitText),
      ).trim()
      : rawSubmitText;
    const submitFileList = isTextSubmit ? [] : successList;

    if ((!trimmed && submitFileList?.length === 0) || disabled || sendDisabled) return;
    if (!isPosterMode && taggingNode && currentSelectedDsModel === undefined) {
      setShowNonSelectDSModelHint(true);
      setTimeout(() => {
        setShowNonSelectDSModelHint(false);
      }, 2000);
      return;
    }
    if (!isTextSubmit && shouldDisableSendForNonImage) return;
    if (isProcessing) return;

    setUploadList([]);
    const messageId = uuidv4();
    const clearTags = (): void => {
      setInputValue('');
      handleSelectedSkillsChange([]);
      handleSelectedKnowledgeChange([]);
      handleClearCharts();
    };

    // 提前计算 modelName（首次和非首次打点均需携带）
    const modelName = currentData?.name_cn;

    if (isFunction(onSubmit)) {
      if (selectedSkills?.length > 0 || selectedCharts?.length > 0) {
        setSessionTemplateMode('skill');
      }
      onSubmit(
        trimmed,
        clearTags,
        submitFileList,
        selectedSkills,
        selectedCharts,
        selectedKnowledge,
        isTextSubmit ? submitFrom : undefined,
        textSubmit?.serverProps,
        modelName ? { model_name: modelName } : undefined,
      );
      if (externalQueryConfig?.form === 'aigallery_report') {
        onCopilotRefTagDismiss?.();
      }
      return;
    }

    const { modelId, locale, name, agentBanner } = currentSelectedDsModel || {};
    const getTemplateParam = (): string => {
      if (textSubmit?.template) return `&template=${textSubmit.template}`;
      if (taggingNode !== undefined && !isPosterMode) return '&template=dsReport';
      if (selectedSkills?.length > 0 || selectedCharts?.length > 0) return '&template=skill';
      if (isPosterMode) return '&template=magicPoster';
      if (serverParams?.function?.name === 'ai_excel') return '&template=aiExcel';
      return '';
    };
    const templateParam = getTemplateParam();
    const { frontend_metadata: externalFrontendMetadata, ...restServerParams } = (serverParams || {}) as Record<string, any>;
    const queryChartsByGame = buildQueryChartsByGame(selectedCharts);
    const hasSkillOrChartPayload =
        (selectedSkills?.length ?? 0) > 0 || queryChartsByGame.length > 0;
    const overriddenFunction = (selectedCharts?.length ?? 0) > 0
      ? { ...(restServerParams.function || {}), name: 'chart', source: 'main_chat' }
      : restServerParams.function;
    const knowledgeParams = buildKnowledgeParams(selectedKnowledge, trimmed);
    const finalServerParams = {
      ...restServerParams,
      ...(overriddenFunction && { function: overriddenFunction }),
      ...(selectedSkills?.length > 0 && {
        skill_config: {
          allowed_skills: allowedSkillsForApi || [],
          specified_skills: selectedSkills.map((s) => ({ name: s.name })),
        },
      }),
      ...(knowledgeParams.db_args && { db_args: knowledgeParams.db_args }),
      frontend_metadata: {
        ...(externalFrontendMetadata || {}),
        // TODO(后续下线): queryTabs 将下线；Skill 展示已改 display_query 内联 `/名`，暂保留写入
        ...(selectedSkills?.length > 0 && { queryTabs: selectedSkills }),
        ...(queryChartsByGame.length > 0 && { queryChartsByGame }),
        ...(trimmed && { display_query: trimmed }),
      },
    };

    const finalModelId = hasSkillOrChartPayload ? SKILL_AGENT_UUID : modelId;
    const hasKnowledgePayload = (selectedKnowledge?.length ?? 0) > 0;
    const hasLogParams = taggingNode || hasSkillOrChartPayload || hasKnowledgePayload
      || (isPosterMode && selectedPosterTemplate);
    const buriedLogExtras = buildLogParams({
      dataSource: 'agent',
      selectedCharts,
      selectedKnowledge,
      kbDocUnit,
    });
    const chartNameLog = buriedLogExtras.chart_name as string | undefined;
    const kbNameLog = buriedLogExtras.kb_name as string | undefined;
    const logParams: Record<string, any> | undefined = {
      model_name: name || currentData?.name_cn,
      ...(hasLogParams ? {
        ...(taggingNode && !isPosterMode && { model_id: finalModelId, system_language: locale }),
        ...(selectedSkills?.length > 0 && { skill_name: selectedSkills.map((s) => s.name).join(',') }),
        ...(kbNameLog && { kb_name: kbNameLog }),
        ...(chartNameLog && { chart_name: chartNameLog }),
        ...(isPosterMode && selectedPosterTemplate && {
          posterTemplateName: isEN
            ? selectedPosterTemplate.titleEn
            : selectedPosterTemplate.titleCn,
        }),
      } : {}),
    };

    history.push({
      pathname: '/agent/chat',
      search: `?sessionId=${submitFrom === 'homeQuestion' ? uuidv4() : initialHomeSessionId}${templateParam}${chatMode ? `&chatMode=${chatMode}` : ''}`,
      state: {
        initialMessage: trimmed,
        messageId,
        initFileList: submitFileList,
        from: submitFrom,
        model_id: finalModelId,
        locale,
        agentBanner,
        agentName: name,
        ...(logParams && { logParams }),
        ...(!isEmpty(finalServerParams) && { serverParams: finalServerParams }),
        ...(selectedSkills?.length > 0 && { selectedSkills }),
        ...(selectedCharts?.length > 0 && { selectedCharts }),
        ...(selectedKnowledge?.length > 0 && { selectedKnowledge }),
      },
    });
    clearTags();
  },
  [
    inputValue,
    successList,
    disabled,
    sendDisabled,
    shouldDisableSendForNonImage,
    isProcessing,
    onSubmit,
    externalQueryConfig?.form,
    onCopilotRefTagDismiss,
    selectedSkills,
    selectedCharts,
    selectedKnowledge,
    setSessionTemplateMode,
    handleSelectedSkillsChange,
    handleSelectedKnowledgeChange,
    handleClearCharts,
    currentSelectedDsModel,
    taggingNode,
    isPosterMode,
    serverParams,
    allowedSkillsForApi,
    isEN,
    selectedPosterTemplate,
    initialHomeSessionId,
    chatMode,
    history,
  ],
  );

  /** 暴露命令式 API：供猜你想问 / 推荐问题走与手动发送相同的提交流程 */
  useImperativeHandle(ref, () => ({
    /** 以外部文案提交，自动携带当前 Skill / Chart */
    submitText: (text: string, options?: IQAInputSubmitTextOptions) => {
      handleSubmit({ text, ...options });
    },
  }), [handleSubmit]);

  const { run: handleStopStreaming } = useThrottleFn(
    async () => {
      try {
        const result = await runAsync({
          user_id: userInfo?.openid,
          session_id: sessionId,
          message_id: messageId,
          trace_id: uuidv4(),
        });
        cancelRequest?.();
        showErrorTips(result?.res_code);
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501014',
          extInfo: JSON.stringify({
            sessionId,
            from,
            messageId
          }),
          typeId: 'aigc',
        });
      } catch (error) {
        //
      }
    },
    { wait: 2000 },
  );

  // 输入法开始
  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  // 输入法结束 (Code Review Fix #8)
  const handleCompositionEnd = () => {
    // 移除 setTimeout，直接重置状态
    // 原 setTimeout 可能导致边界情况：在重置前用户可能触发其他事件
    composingRef.current = false;
  };



  /**
   * 光标在最前方时，仅处理海报/DS 等 taggingNode；
   * Skill/KB/Chart 已改为正文内引用串，由用户直接编辑删除。
   */
  const tryRemoveTagOnBackspace = (): boolean => {
    const surface = editorRef.current;
    if (!surface) return false;
    if (
      composingRef.current
      || surface.selectionStart !== 0
      || surface.selectionStart !== surface.selectionEnd
    ) return false;

    // 删除标签（taggingNode），由上层组件根据页签控制具体逻辑
    if (taggingNode && onTaggingNodeClear) {
      onTaggingNodeClear();
      // 魔法海报：如果输入框中是默认文案（跟随模板自动填充的），一并清空
      if (isPosterMode) {
        const trimmed = inputValue.trim();
        if (trimmed === POSTER_DEFAULT_TEXT_ZH.trim() || trimmed === POSTER_DEFAULT_TEXT_EN.trim()) {
          setInputValue('');
        }
      }
      return true;
    }

    return false;
  };

  const textAreaKeyDownHandler = (event: KeyboardEvent) => {
    const surface = editorRef.current;
    if (!surface) return;

    // / 模式下拉框打开时，将 ArrowUp/Down/Enter/Escape 转发给 keyboard hook 处理
    if (handleSlashModeKeyDown(event)) return;

    // @ 模式：阻止 Enter 提交 / 方向键移动光标（选中由 KnowledgeBaseMention 处理）
    if (
      isKbAtModeOpen
      && (event.key === 'Enter'
        || event.key === 'ArrowUp'
        || event.key === 'ArrowDown'
        || event.key === 'Escape')
    ) {
      if (!event.defaultPrevented) {
        event.preventDefault();
      }
      return;
    }

    // Backspace 到头时按优先级删除标签（魔法海报模板 → DS 报告）
    if (event.key === 'Backspace' && tryRemoveTagOnBackspace()) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      if (isMentionEditorSurface(surface)) {
        document.execCommand('insertText', false, '\t');
        return;
      }
      const textArea = surface as HTMLTextAreaElement;
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      const { scrollTop } = textArea;
      textArea.value = `${textArea.value.substring(0, start)}\t${textArea.value.substring(end)}`;
      textArea.selectionStart = start + 1;
      textArea.selectionEnd = start + 1;
      textArea.scrollTop = scrollTop;
      return;
    }

    if (event.key === 'Enter' && !composingRef.current) {
      if (event.defaultPrevented) return;

      const isDuringComposition = composingRef.current || event.isComposing;

      if (disabled || (!isPosterMode && taggingNode && currentSelectedDsModel === undefined)) {
        setShowNonSelectDSModelHint(true);

        setTimeout(() => {
          setShowNonSelectDSModelHint(false);
        }, 2000);
      }

      if (isDuringComposition) {
        setTimeout(() => {
          if (!composingRef.current && !event.shiftKey) {
            handleSubmit();
          }
        }, 0);
        return;
      }
      if (event.altKey) {
        event.preventDefault();
        if (isMentionEditorSurface(surface)) {
          document.execCommand('insertText', false, '\n');
        } else {
          const textArea = surface as HTMLTextAreaElement;
          const start = textArea.selectionStart;
          const end = textArea.selectionEnd;
          textArea.value = `${textArea.value.slice(0, start)}\n${textArea.value.slice(end)}`;
          textArea.selectionStart = start + 1;
          textArea.selectionEnd = start + 1;
        }
      } else if (!event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    }
  };

  useLayoutEffect(() => {
    if (tagRef.current && taggingNode) {
      setTagIndent(tagRef.current.offsetWidth);
    } else setTagIndent(0);
  }, [taggingNode]);

  // key 绑定模板 ID，确保切换模板时 React 完整卸载/挂载标签节点，避免视觉与 DOM 不一致
  const clonedTag = taggingNode !== undefined && React.cloneElement(taggingNode, {
    key: (taggingNode as React.ReactElement)?.key ?? 'tag',
    ref: tagRef,
  });

  return (
    <>
      {customHeader}
      <div
        ref={inputBoxContainerRef}
        className={classNames('dba-qa-input', className, {
          ['dba-input-active']: isFocused || inputValue,
        })}
        style={style}
      >
        {
          visibleUploadList?.length > 0 && (
            <div className='dba-qa-input-img-wrapper'>
              <ImgUploadRenderList
                uploadList={uploadList}
                isShowSwiper={from === 'copilot'}
                // setUploadLists={setUploadLists}
                // handleFileUpload={handleFileUpload}
                handleReUpload={handleReUpload}
                handleRemoveItem={handleRemoveItem}
              />
            </div>
          )
        }
        <ExternalRefCard
          form={externalQueryConfig?.form}
          text={copilotRefTagText}
          sessionId={sessionId}
          showClose={copilotRefTagShowClose}
          hideArrow={copilotRefTagHideArrow}
          imgSrc={copilotRefTagImage}
          onDismiss={onCopilotRefTagDismiss}
        />
        {hasChartTags && (
          <div className="dba-qa-input-chart-tags">
            {selectedCharts.map((chart) => (
              <SelectedChartTag
                key={chart.uniqueKey}
                chart={chart}
                onClose={handleRemoveChartTag}
                onClick={handleChartTagClick}
              />
            ))}
          </div>
        )}
        <div ref={editorRowRef} className="dba-qa-input-editor-row">
          {/* DS 报告标签 —— 放入 editor-row 内，z-index 在 textarea 之上，避免被绝对定位的 textarea-wrap 遮盖 */}
          {clonedTag !== undefined && clonedTag}
          {/* contentEditable 编辑器：@ / 为原子 mention 节点 */}
          <div className="dba-qa-input-textarea-wrap">
            <MentionEditor
              key={
                isEdit
                  ? `edit-${currentConversation?.messageId ?? 'history'}`
                  : 'default'
              }
              ref={editorRef}
              value={inputValue}
              onChange={handleEditorChange}
              placeholder={placeholderMessage}
              maxLength={CHAT_BI_MAX_TEXT_COUNT}
              maxHeight={maxHeight}
              minHeight={minHeight}
              disabled={disabled}
              isEN={isEN}
              kbDocUnit={kbDocUnit}
              selectedKnowledge={selectedKnowledge}
              selectedSkills={selectedSkills}
              freezeMentionLabels={isEdit}
              textIndent={
                tagIndent > 0
                  ? `${tagIndent + (isCopilot ? 0 : 10)}px`
                  : undefined
              }
              onFocus={() => {
                setIsFocused(true);
                handleMentionFocusOrClick();
              }}
              onBlur={() => setIsFocused(false)}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onKeyDown={(e) => {
                textAreaKeyDownHandler(e as unknown as KeyboardEvent);
              }}
              onPaste={handlePaste}
            />
          </div>
        </div>

        {/* 底部操作栏（正常文档流布局） */}
        <div className="dba-qa-input-footer">
          {/* 左侧按钮组（流式布局，包含 Skill 标签） */}
          <div className='dba-qa-input-button-wrapper-left'>
            <UploadFileButton
              visible={
                !isRecording
                && (template !== 'dsReport' && (!taggingNode || isPosterMode))
                && (isShowDownload || isPosterMode)
                && !currentData?.hideFileUpload
              }
              disableFileUpload={!!disableFileUpload}
              imgUploadDisabled={imgUploadDisabled}
              agentName={isEN ? currentData?.name : currentData?.name_cn}
              onClick={() => handleClickUpload()}
            />
            {/* Agent 选择器和思考模式选择器 */}
            <AgentModeSelector
              isRecording={isRecording}
              template={template as string | undefined}
              effectiveTemplate={effectiveTemplate}
              isMentionScene={isMentionScene}
              taggingNode={taggingNode}
              canChangeMode={canChangeMode}
              sessionId={sessionId}
              from={from}
              lastMessages={lastMessages}
              messageId={messageId}
              selectedSkills={selectedSkills}
              onSelectedSkillsChange={handleSelectedSkillsChange}
              isPosterMode={isPosterMode}
              {...skillMentionHostProps}
              {...chartMentionHostProps}
              {...knowledgeMentionHostProps}
              inputBoxContainerRef={inputBoxContainerRef}
              isEdit={isEdit}
              externalHighlightIndex={slashHighlightIndex}
              onFilteredSkillsChange={handleFilteredSkillsChange}
              onSlashModeSelect={handleSlashModeSelect}
              onAtModeSelect={handleKbAtModeSelect}
              onKbLabelReplace={handleKbLabelReplace}
              onKbBeforePanelOpen={handleKbBeforePanelOpen}
              onKbInsertAtCursor={handleKbInsertAtCursor}
              onKbPanelSync={handleKbPanelSync}
              onKbPanelCommit={handleKbPanelCommit}
              selectedKnowledge={selectedKnowledge}
              onSelectedKnowledgeChange={handleSelectedKnowledgeChange}
              onChartConfirm={handleChartConfirm}
              chartPopoverRef={chartPopoverRef}
              selectedCharts={selectedCharts}
            />
          </div>

          {/* 右侧操作按钮组 */}
          <div className="dba-input-options">
            <VoiceRecorder
              state={{
                isRecording,
                speekLoading,
                voiceCountdownText,
              }}
              style={{
                voiceActiveBar,
                custVoiceStyle,
              }}
              onVoiceInput={() => { void handleVoiceInput() }}
              onConfirmRecording={confirmRecording}
              onCancelRecording={cancelRecording}
            />
            <SubmitActions
              state={{
                isRecording,
                isEdit,
                isProcessing,
                disabled,
                retrying,
                isCopilot,
                isPosterMode,
              }}
              sendConfig={{
                hasInput: !!inputValue.trim(),
                successListLength: successList?.length,
                sendDisabled,
                shouldDisableSendForNonImage,
                stopLoading: loading,
              }}
              context={{
                taggingNode,
                currentSelectedDsModel,
                showNonSelectDSModelHint,
              }}
              onCancelEdit={handleCancelEdit}
              onStopStreaming={() => { void handleStopStreaming() }}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
      <DragDropUpload isVisible={isDragging && (taggingNode === undefined || isPosterMode) && !disableFileUpload} popupContainer={dropAreaRef?.current} />
    </>
  );
});

QAInput.displayName = 'QAInput';

export type {
  IQAInputProps,
  IQAInputRef,
  IQAInputSubmitTextOptions,
  IQAInputSubmitServerProps,
  TQAInputOnSubmit,
} from './types';

export { createQAInputOnSubmit } from './utils/createQAInputOnSubmit';
export type {
  ICreateQAInputOnSubmitConfig,
  IHandleSubmitMentionConfig,
  IHandleSubmitPayload,
  IHandleSubmitServerProps,
  IPageHandleSubmitOptions,
  TPageHandleSubmit,
} from './utils/createQAInputOnSubmit';

export default QAInput;
